import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type {
	ReactionLocation,
	QueryData,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import {
	getReactionsQueryInfo,
	getReactionsSummary,
} from '@confluence/reactions/entry-points/reactionQueryHelpers.ts';
import { getBooleanLocationTypes } from '@confluence/reactions/entry-points/reactionLocationUtils.ts';

export interface OmnibarReactionsData {
	reactions: Array<{
		ari: string;
		containerAri: string;
		emojiId: string;
		count: number;
		reacted: boolean;
		users: never[];
	}>;
	totalCount: number;
	ari: string;
	containerAri: string;
}

const EMPTY_REACTIONS_DATA: OmnibarReactionsData = {
	reactions: [],
	totalCount: 0,
	ari: '',
	containerAri: '',
};

/**
 * Transforms raw GraphQL reactions data to the expected format.
 */
const transformReactionsData = (
	rawReactionsData: QueryData | null | undefined,
): OmnibarReactionsData => {
	const summary = getReactionsSummary(rawReactionsData);
	if (!summary) {
		return EMPTY_REACTIONS_DATA;
	}
	const reactionNodes = summary.reactionsSummaryForEmoji || [];
	const reactions: OmnibarReactionsData['reactions'] = [];
	for (const node of reactionNodes) {
		if (node && node !== null) {
			reactions.push({
				ari: summary.ari || '',
				containerAri: summary.containerAri || '',
				emojiId: node.emojiId ?? '',
				count: node.count ?? 0,
				reacted: node.reacted || false,
				users: [],
			});
		}
	}
	return {
		reactions,
		totalCount: summary.reactionsCount || 0,
		ari: summary.ari || '',
		containerAri: summary.containerAri || '',
	};
};

interface UseOmnibarReactionsDataProps {
	contentId: string;
	spaceId: string;
	contentType: string;
	skip: boolean;
}

interface UseOmnibarReactionsDataResult {
	reactionsData: OmnibarReactionsData;
	isLoading: boolean;
}

export const useOmnibarReactionsData = ({
	contentId,
	spaceId,
	contentType,
	skip,
}: UseOmnibarReactionsDataProps): UseOmnibarReactionsDataResult => {
	const queryInfo = useMemo(() => {
		if (skip) {
			return null;
		}
		try {
			const { isComment } = getBooleanLocationTypes(contentType as ReactionLocation);
			return getReactionsQueryInfo(isComment, contentId, spaceId);
		} catch {
			return null;
		}
	}, [skip, contentType, contentId, spaceId]);

	const {
		data: rawReactionsData,
		loading,
		error: reactionsError,
	} = useQueryInterop<QueryData, Record<string, any>>(
		(queryInfo?.query ?? {}) as unknown as GraphQLTaggedNode,
		{
			variables: queryInfo?.queryVariables,
			fetchPolicy: 'cache-only',
			errorPolicy: 'all',
			skip: skip || !queryInfo?.queryVariables,
		},
	);

	const reactionsData = useMemo(() => {
		if (reactionsError || !rawReactionsData) {
			return EMPTY_REACTIONS_DATA;
		}
		return transformReactionsData(rawReactionsData);
	}, [rawReactionsData, reactionsError]);

	return { reactionsData, isLoading: loading };
};
